"use client";

import {
  type FC,
  type Ref,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Analysis, type AnalysisProps } from "@huub-web/shared";
import {
  PhotoSelectionModal,
  usePhotos,
} from "@huub-web/shared/components/PhotoSelectionModal";
import { addParamsToURL } from "@huub-web/utils";
import {
  AnalysisSectionIndex,
  analysisSectionIndexLabels,
} from "@huub-web/shared/components/Analysis/types/AnalysisSections";
import {
  Body,
  Button,
  Flex,
  Icon,
  Modal,
  ProgressBar,
  type UIAction,
  useActions,
  usePubSub,
  type UserPromptRecord,
} from "@huub-web/ui";
import type { WebSocketProgressUpdateMessage } from "@huub-web/backend";

import { useProgressUpdate, useTemplates } from "../../hooks";
import { PageContentContext } from "../../contexts/PageContentContext";
import type { GeoJSONType } from "@huub-web/shared/components/Analysis/Analysis";
import { interiorDesignerButtonStyle } from "./AnalysisTemplate.css";

export type AnalysisTemplateProps = {
  unlisted?: AnalysisProps["unlisted"];
  record: AnalysisProps["record"];
  center: AnalysisProps["center"];
  images: AnalysisProps["images"];

  saveEndpoint?: string;
  deleteEndpoint?: string;
  saved?: boolean;

  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
  CTAs?: {
    maximumMortgage?: {
      label?: string;
      action: UIAction;
      tracking?: string;
    };
    chance?: {
      label?: string;
      action: UIAction;
      tracking?: string;
    };
    realisticOffer?: {
      label?: string;
      action: UIAction;
      tracking?: string;
    };
  };

  defaultProgress?: WebSocketProgressUpdateMessage["data"]["progress"];
  analysis?: AnalysisProps["analysis"];

  geoJSONEndpoint: string;
  /**
   * walter_address_object_id for the property, used to fetch
   * Interior Designer photos. Injected by backend.
   */
  walterAddressObjectId?: string;
};

/**
 * AnalysisTemplate component
 */
export const AnalysisTemplate: FC<AnalysisTemplateProps> = ({
  record,
  saveEndpoint,
  deleteEndpoint,
  saved: savedProp,
  images,
  center,
  analysis: analysisProp,
  CTAs,
  unlisted,
  geoJSONEndpoint,
  defaultProgress = [],
  walterAddressObjectId,
  ref,
}) => {
  const { t } = useTranslation();
  const { $get, $post, $delete, lockUI, env } = useTemplates();
  const pubSub = usePubSub();
  const { handleAction } = useActions();

  const [analysis, setAnalysis] = useState<AnalysisProps["analysis"]>(
    analysisProp ?? {},
  );

  const { progress: loaderSteps } = useProgressUpdate({
    entityID: record.id,
    entity: "house-analysis",
    defaultProgress,
    scope: ["status", "data"],
    onDataUpdate: (data) => {
      setAnalysis((prev) => ({
        ...prev,
        ...data,
      }));
    },
  });

  const [isDeleteConfirmationActive, setIsDeleteConfirmationActive] =
    useState(false);

  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(savedProp);

  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const {
    photos,
    loading: photosLoading,
    error: photosError,
    fetch: fetchPhotos,
  } = usePhotos({ $get, walterId: walterAddressObjectId });

  const handleInteriorDesignerClick = () => {
    fetchPhotos();
    setIsPhotoModalOpen(true);
  };

  const { registerContent, deregisterContent } = useContext(PageContentContext);

  // Register analysis section navigation items as page content
  const hasObservations = !!analysis?.observations;
  useEffect(() => {
    if (!hasObservations) return;
    const sectionIndexes = Object.values(AnalysisSectionIndex).filter(
      (v): v is AnalysisSectionIndex => typeof v === "number",
    );
    const ids: string[] = [];
    for (const index of sectionIndexes) {
      const label = analysisSectionIndexLabels[index];
      if (!label) continue;
      const contentId = `analysis-section-${index}`;
      registerContent(contentId, {
        type: "cta",
        label,
        action: {
          type: "scrollTo" as const,
          payload: { elementId: `analysisSection${index}` },
        },
      });
      ids.push(contentId);
    }
    return () => {
      for (const id of ids) deregisterContent(id);
    };
  }, [hasObservations, registerContent, deregisterContent]);

  const handleSaveToggle = () => {
    if (!saveEndpoint && !deleteEndpoint) {
      console.warn(
        "[AnalysisTemplate] No delete and/or save endpoint provided, skipping",
      );
      return;
    }

    if (!record.id) {
      console.error(
        "[AnalysisTemplate] Cannot update saved state: record.id is missing",
      );
      return;
    }

    const newState = !saved;

    if (newState) {
      if (!saveEndpoint) return;

      setLoading(true);

      $post(saveEndpoint, { id: record.id, saved: true })
        .then(() => {
          setSaved(true);
        })
        .catch((error) => {
          console.error("[AnalysisTemplate] Failed to save house", error);
          pubSub.publish("snackbar", {
            variant: "error",
            body: t("templating.property.saveError"),
          });
          setSaved(false);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      if (!deleteEndpoint) {
        console.warn(
          "[AnalysisTemplate] No delete endpoint provided, cannot delete",
        );
        pubSub.publish("snackbar", {
          variant: "error",
          body: t("templating.property.deleteNotAllowed"),
        });
        return;
      }
      setIsDeleteConfirmationActive(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleteEndpoint) {
      console.error("[AnalysisTemplate] Delete endpoint is missing");
      setIsDeleteConfirmationActive(false);
      return;
    }

    setLoading(true);

    // Construct the delete URL with the record ID
    $delete(deleteEndpoint)
      .then(() => {
        setSaved(false);
      })
      .catch((error) => {
        console.error("[AnalysisTemplate] Failed to delete house", error);
        pubSub.publish("snackbar", {
          variant: "error",
          body: t("templating.property.deleteError"),
        });
      })
      .finally(() => {
        setLoading(false);
        setIsDeleteConfirmationActive(false);
      });
  };

  const savable = (!!saveEndpoint && !saved) || (!!deleteEndpoint && saved);

  const handleActionClick = (action?: UIAction) => {
    if (action) handleAction(action);
  };

  const handlePrompt = (prompt: UserPromptRecord) => {
    if (!prompt) {
      return;
    }
    pubSub.publish("sendPrompt", { value: prompt });
  };

  const CTANodes: AnalysisProps["CTAs"] = {
    maximumMortgage: CTAs?.maximumMortgage?.action ? (
      <Button
        onClick={() => {
          handleActionClick(CTAs?.maximumMortgage?.action);
        }}
        alignSelf="flex-start"
        tracking={
          CTAs?.maximumMortgage?.tracking ??
          "analysis_summary_maximum_mortgage_button"
        }
      >
        {CTAs?.maximumMortgage?.label ?? t("analysis.calculateMaxMortgage")}
      </Button>
    ) : null,
    chance: (
      <Button
        variant="background"
        onClick={() => {
          handleActionClick(CTAs?.chance?.action);
        }}
        tracking={CTAs?.chance?.tracking ?? "analysis_summary_chance_button"}
      >
        {CTAs?.chance?.label ?? t("analysis.freeBidCertificate")}
      </Button>
    ),
    realisticOffer: CTAs?.realisticOffer?.action ? (
      <Button
        alignSelf="flex-start"
        onClick={() => {
          handleActionClick(CTAs?.realisticOffer?.action);
        }}
      >
        {CTAs?.realisticOffer?.label ?? t("analysis.discussWithAdvisor")}
      </Button>
    ) : null,
  };

  const getGeoJSON = useCallback(
    async (type: GeoJSONType) => {
      const response = await $get(
        (geoJSONEndpoint ?? "/geojson/[type]").replace("[type]", type),
      );
      return response?.data;
    },
    [$get, geoJSONEndpoint],
  );

  return (
    <>
      <Analysis
        ref={ref}
        lockUI={lockUI}
        CTAs={CTANodes}
        analysis={analysis}
        record={record}
        unlisted={unlisted}
        center={center}
        images={images}
        onPrompt={handlePrompt}
        onSaveToggle={handleSaveToggle}
        savable={savable ?? false}
        mapBoxToken={env.mapBoxToken}
        contentMenuVariant="app"
        loaderSteps={loaderSteps}
        saved={saved}
        getGeoJSON={getGeoJSON}
        interiorDesignerAction={
          walterAddressObjectId && images && images.length > 0 ? (
            <Button
              variant="opaque"
              border="default"
              size="s"
              align="center"
              leading={<Icon icon="paintRoller" size="s" />}
              onClick={handleInteriorDesignerClick}
              className={interiorDesignerButtonStyle}
            >
              {t("photoSelection.triggerButton")}
            </Button>
          ) : undefined
        }
      />

      {isDeleteConfirmationActive && (
        <Modal placement="center">
          {loading && <ProgressBar variant="infinite" position="absolute" />}

          <Body>
            {t("templating.property.deleteConfirmation", {
              street: record.street,
            })}
          </Body>

          <Flex justify="space-between" align="flex-end" gap="s">
            <Button
              rel="noopener noreferrer"
              variant="background"
              border="default"
              onClick={() => setIsDeleteConfirmationActive(false)}
              disabled={loading}
            >
              {t("cancel")}
            </Button>
            <Button
              rel="noopener noreferrer"
              variant="error"
              trailing={<Icon icon="delete" size="s2" />}
              onClick={handleDeleteConfirm}
              disabled={loading}
            >
              {t("delete")}
            </Button>
          </Flex>
        </Modal>
      )}

      {isPhotoModalOpen && (
        <PhotoSelectionModal
          photos={photos}
          loading={photosLoading}
          error={photosError}
          selectedPhotoId={selectedPhotoId}
          address={`${record.street}, ${record.city}`}
          onSelect={({ photoId }) => setSelectedPhotoId(photoId)}
          onClose={() => {
            setIsPhotoModalOpen(false);
            setSelectedPhotoId(null);
          }}
          onConfirm={({ photoId }) => {
            setIsPhotoModalOpen(false);
            const basePath = window.location.pathname.replace(/\/$/, "");
            pubSub.publish("navigateToPath", {
              path: addParamsToURL(`${basePath}/interior-designer`, {
                photoId,
              }),
            });
          }}
          onRetry={fetchPhotos}
        />
      )}
    </>
  );
};
